import { Button, TextField } from "@ths/ui";
import type { FC, FormEvent } from "react";
import { useEffect, useRef, useState } from "react";

import type {
  ReminderOptions,
  CheckInContact,
} from "../components/ReminderConsentFields";
import Spinner from "../components/Spinner";
import type { SelfCheckinLookupReservation } from "../generated/graphql";

import CheckInStep, { ArrivalStaySummary } from "./CheckInStep";
import { formatEmailInput, getEmailValidationError } from "./emailFormatting";
import {
  formatPhoneInput,
  getRequiredPhoneValidationError,
  normalizePhoneForSubmit,
} from "./phoneFormatting";

export type ArrivalInput = Omit<CheckInContact, "email"> & {
  email?: string;
  phone?: string;
  carPlateNumber?: string;
  carMake?: string;
};

interface VehicleStepProps {
  isSubmitting: boolean;
  error: string;
  initialPhone?: string | null;
  stay?: Pick<
    SelfCheckinLookupReservation,
    "basecampName" | "checkInDate" | "checkOutDate" | "checkedInAt"
  >;
  showStaySummary?: boolean;
  requiresEmail?: boolean;
  initialEmail?: string;
  collectReminders?: boolean;
  initialSmsReminderStatus?: string | null;
  reminderOptions?: ReminderOptions;
  reminderOptionsLoading?: boolean;
  onRetryReminderOptions?: () => void;
  onSubmit: (input: ArrivalInput) => void;
  onBack: () => void;
}

/**
 * Stages shown during the check-in mutation. Reflects the actual server work
 * — selfCheckin resolves the room assignment, derives access codes, and
 * returns the check-in payload. Cycling text gives the guest a sense of
 * progress instead of staring at a static spinner for ~1-2s.
 */
const CHECKIN_STAGES = [
  "Checking you in…",
  "Locating your room…",
  "Preparing your room details…",
] as const;
const STAGE_DURATION_MS = 1300;

const inputClassName =
  "h-12 rounded-xl border-on-navy/30 bg-on-navy/5 text-on-navy placeholder:text-on-navy/60 focus:ring-on-navy";

const CheckingInView: FC = () => {
  const [stageIdx, setStageIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      // Clamp at the last stage rather than wrap — if the server takes
      // longer than the full cycle we sit on "Preparing your room details…"
      // instead of looping back, which would feel like it's stuck.
      setStageIdx((i) => Math.min(i + 1, CHECKIN_STAGES.length - 1));
    }, STAGE_DURATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="bc-fade-up flex flex-col items-center gap-8 py-14"
      role="status"
      aria-live="polite"
      aria-label={CHECKIN_STAGES[stageIdx]}
    >
      <Spinner className="size-10 text-on-navy" />

      {/* Cycling stage label. Absolute-positioned so the crossfade doesn't
          shift surrounding layout. Width is wide enough for the longest
          stage so the container doesn't reflow on text change. */}
      <div className="relative h-7 w-72">
        {CHECKIN_STAGES.map((stage, i) => (
          <p
            key={stage}
            className={`absolute inset-0 text-center font-oswald text-lg tracking-wide text-on-navy/75 transition-opacity duration-500 ${
              i === stageIdx ? "opacity-100" : "opacity-0"
            }`}
          >
            {stage}
          </p>
        ))}
      </div>
    </div>
  );
};

const VehicleStep: FC<VehicleStepProps> = ({
  isSubmitting,
  error,
  initialPhone = "",
  stay,
  showStaySummary = false,
  requiresEmail = false,
  initialEmail = "",
  collectReminders = false,
  initialSmsReminderStatus,
  reminderOptions,
  reminderOptionsLoading,
  onRetryReminderOptions,
  onSubmit,
  onBack,
}) => {
  const [contactVisited] = useState(() =>
    Boolean(
      requiresEmail || getRequiredPhoneValidationError(initialPhone ?? ""),
    ),
  );
  const [stage, setStage] = useState<"contact" | "vehicle" | "checkin">(() =>
    requiresEmail || getRequiredPhoneValidationError(initialPhone ?? "")
      ? "contact"
      : "vehicle",
  );
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousStage = useRef(stage);
  const wasSubmitting = useRef(isSubmitting);
  useEffect(() => {
    const returnedFromSubmission = wasSubmitting.current && !isSubmitting;
    wasSubmitting.current = isSubmitting;
    if (previousStage.current === stage && !returnedFromSubmission) return;
    previousStage.current = stage;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    headingRef.current?.focus({ preventScroll: true });
  }, [stage, isSubmitting]);
  const [droveVehicle, setDroveVehicle] = useState<boolean | null>(null);
  const [phone, setPhone] = useState(() =>
    formatPhoneInput(initialPhone ?? ""),
  );
  const [smsConsent, setSmsConsent] = useState(false);
  const [reminderPromptSeen, setReminderPromptSeen] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [email, setEmail] = useState(() => formatEmailInput(initialEmail));
  const [emailTouched, setEmailTouched] = useState(false);
  const [plate, setPlate] = useState("");
  const [make, setMake] = useState("");
  const submitLock = useRef(false);
  useEffect(() => {
    if (!isSubmitting) submitLock.current = false;
  }, [isSubmitting]);

  const trimmedPlate = plate.trim();
  const trimmedMake = make.trim();
  const smsEnrolled =
    collectReminders && reminderOptions?.smsAvailable && smsConsent;
  const phoneValidationError = getRequiredPhoneValidationError(phone);
  const emailValidationError = requiresEmail
    ? getEmailValidationError(email)
    : "";
  const canContinue =
    droveVehicle !== null &&
    (!droveVehicle || Boolean(trimmedPlate && trimmedMake)) &&
    !isSubmitting;
  const canSubmit = canContinue;

  const handleContinue = (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue) return;
    setStage("checkin");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setPhoneTouched(true);
    setEmailTouched(true);
    if (
      stage !== "checkin" ||
      !canSubmit ||
      emailValidationError ||
      phoneValidationError ||
      submitLock.current
    )
      return;
    submitLock.current = true;
    onSubmit({
      phone: normalizePhoneForSubmit(phone)!,
      ...(collectReminders && reminderOptions?.enabled
        ? {
            smsConsent: Boolean(smsEnrolled),
            smsPromptDismissed: reminderPromptSeen && !smsEnrolled,
          }
        : {}),
      ...(smsEnrolled
        ? {
            smsConsent: true,
            smsDisclosureVersion: reminderOptions.disclosureVersion,
          }
        : {}),
      ...(requiresEmail ? { email } : {}),
      ...(droveVehicle
        ? {
            carPlateNumber: trimmedPlate,
            carMake: trimmedMake,
          }
        : {}),
    });
  };

  if (isSubmitting) {
    return (
      <div className="w-full max-w-sm">
        <CheckingInView />
      </div>
    );
  }

  if (stage === "contact") {
    return (
      <form
        noValidate
        className="bc-fade-up w-full max-w-sm space-y-8 text-on-navy"
        onSubmit={(event) => {
          event.preventDefault();
          setPhoneTouched(true);
          setEmailTouched(true);
          if (!phoneValidationError && !emailValidationError)
            setStage("vehicle");
        }}
      >
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-center font-oswald text-3xl font-medium uppercase tracking-wide focus:outline-none sm:text-4xl"
        >
          Your contact details
        </h1>
        <div className="space-y-6 [&_label]:text-on-navy/80 [&_p]:text-error-surface">
          <TextField
            label="Your Phone"
            required
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={phone}
            onChange={(event) => {
              setPhone(formatPhoneInput(event.target.value));
              setSmsConsent(false);
            }}
            onBlur={() => setPhoneTouched(true)}
            maxLength={24}
            placeholder="(555) 555-5555"
            error={phoneTouched ? phoneValidationError : undefined}
            className={inputClassName}
          />

          {requiresEmail && (
            <TextField
              label="Your Email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) =>
                setEmail(formatEmailInput(event.target.value))
              }
              onBlur={() => setEmailTouched(true)}
              error={emailTouched ? emailValidationError : undefined}
              placeholder="name@example.com"
              className={inputClassName}
            />
          )}
        </div>
        <p className="text-sm text-on-navy/70">
          THS staff may use this number to contact you about your stay.
        </p>
        <Button
          type="submit"
          variant="ghost"
          size="lg"
          className="h-auto min-h-12 w-full rounded-xl bg-highlight px-8 py-4 font-oswald text-lg font-medium uppercase tracking-wider text-primaryDarkBlue shadow-lg shadow-highlight/20 hover:bg-highlight focus-visible:ring-on-navy"
        >
          Continue
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="h-auto w-full py-3 text-sm text-on-navy/70 hover:bg-transparent hover:text-on-navy"
        >
          Go Back
        </Button>
      </form>
    );
  }

  if (stage === "checkin") {
    return (
      <CheckInStep
        headingRef={headingRef}
        stay={stay}
        vehicle={
          droveVehicle ? `${trimmedMake} · ${trimmedPlate}` : "No vehicle"
        }
        collectReminders={collectReminders}
        reminderOptions={reminderOptions}
        reminderOptionsLoading={reminderOptionsLoading}
        onRetryReminderOptions={onRetryReminderOptions}
        smsConsent={smsConsent}
        reminderPromptSeen={reminderPromptSeen}
        onReminderPromptSeen={() => setReminderPromptSeen(true)}
        initialSmsReminderStatus={
          normalizePhoneForSubmit(phone) ===
          normalizePhoneForSubmit(initialPhone ?? "")
            ? initialSmsReminderStatus
            : undefined
        }
        onSmsConsentChange={setSmsConsent}
        error={error}
        canSubmit={canSubmit}
        onSubmit={handleSubmit}
        onBack={() => setStage("vehicle")}
      />
    );
  }

  return (
    <form
      noValidate
      onSubmit={handleContinue}
      className="bc-fade-up w-full max-w-sm space-y-8 text-on-navy"
    >
      <header className="space-y-6 text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          id="vehicle-question"
          className="break-words font-oswald text-3xl font-medium uppercase tracking-wide focus:outline-none sm:text-4xl"
        >
          Did you drive your own vehicle here?
        </h1>
        {showStaySummary && stay && <ArrivalStaySummary stay={stay} />}
      </header>
      <div
        role="group"
        aria-labelledby="vehicle-question"
        className="grid grid-cols-2 gap-4"
      >
        {[false, true].map((value) => (
          <Button
            key={String(value)}
            type="button"
            variant="ghost"
            size="lg"
            aria-pressed={droveVehicle === value}
            onClick={() => setDroveVehicle(value)}
            className={`h-auto rounded-xl border px-8 py-5 font-oswald text-lg font-medium uppercase tracking-wider focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong active:scale-[0.98] ${droveVehicle === value ? "border-transparent bg-highlight text-primaryDarkBlue shadow-lg shadow-highlight/20 hover:bg-highlight" : "border-on-navy/15 bg-transparent text-on-navy/70 hover:border-on-navy/30 hover:bg-transparent hover:text-on-navy"}`}
          >
            {value ? "Yes" : "No"}
          </Button>
        ))}
      </div>
      {droveVehicle && (
        <div className="space-y-6 [&_label]:text-on-navy/80 [&_p]:text-error-surface">
          <TextField
            label="Plate Number"
            autoComplete="off"
            value={plate}
            onChange={(event) => setPlate(event.target.value.toUpperCase())}
            maxLength={10}
            placeholder="ABC1234"
            className={inputClassName}
          />
          <TextField
            label="Vehicle Make"
            autoComplete="off"
            value={make}
            onChange={(event) => setMake(event.target.value)}
            maxLength={40}
            placeholder="Toyota"
            className={inputClassName}
          />
        </div>
      )}

      <Button
        type="submit"
        variant="ghost"
        size="lg"
        disabled={!canContinue}
        className={`h-auto min-h-12 w-full rounded-xl px-8 py-4 font-oswald text-lg font-medium uppercase tracking-wider focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong active:scale-[0.98] disabled:opacity-100 ${canContinue ? "bg-highlight text-primaryDarkBlue shadow-lg shadow-highlight/20 hover:bg-highlight" : "bg-on-navy/[0.06] text-on-navy/50 hover:bg-on-navy/[0.06]"}`}
      >
        Continue
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() =>
          contactVisited || requiresEmail ? setStage("contact") : onBack()
        }
        className="h-auto w-full py-3 text-sm font-medium text-on-navy/25 hover:bg-transparent hover:text-on-navy/40 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
      >
        Go Back
      </Button>
    </form>
  );
};

export default VehicleStep;
